import React, { Children, useEffect, useLayoutEffect, useRef, useState } from "react";
import Svg from "../../Components/Svg";
import { translate } from "../../Services/translation";
import clsx from "clsx";

const scrollNavigationStyles = Object.freeze(['style-1', 'style-2', 'hidden']);

/**
 * A horizontal scrolling container with drag-to-scroll functionality
 * @constructor
 * @param {Object} props The component props
 * @param {Array<{breakpoint: number, settings: {itemsToShow: number}}>} props.responsiveConfig Array of responsive breakpoint configurations
 * @param {React.ReactNode} props.children Child elements to be rendered in the scroll container
 * @param {string|null} [props.heading=null] - Heading text
 * @param {string|null} [props.scrollNavigationStyle=style-1] - style-1, style-2
 * @param {boolean} [props.hideScrollBar=false] - hide scroll bar, default value is `false`
 * @param {React.ReactNode|null} [props.prevIcon] - React.ReactNode for previous icon, used in previous button.
 * @param {React.ReactNode|null} [props.nextIcon] - React.ReactNode for next icon, used in next button.
 * @returns {JSX.Element} The ScrollContainer component
 */
const ScrollContainer = function ({
    responsiveConfig,
    children,
    scrollNavigationStyle = 'style-1',
    hideScrollBar = false,
    heading = null,
    prevIcon = null,
    nextIcon = null,
    onPrev = null,
    onNext = null
}) {
    const [gapSize, setGapSize] = useState(16);
    const dragThreshold = 5;
    const numberOfChildren = Children.count(children);

    // Validate for invalid styles
    if (!scrollNavigationStyles.includes(scrollNavigationStyle)) {
        scrollNavigationStyle = 'style-1';
    }

    /**
     * @type {React.MutableRefObject<HTMLDivElement>}
     */
    const innerElementRef = useRef();

    /**
     * @type {React.MutableRefObject<Number>}
     */
    const resizeTimeoutRef = useRef(0);

    /**
     * @type {React.MutableRefObject<boolean>}
     */
    const isDragging = useRef(false);

    /**
     * @type {React.MutableRefObject<Number>}
     */
    const dragStartX = useRef(0);

    /**
     * @type {React.MutableRefObject<Number>}
     */
    const scrollStartX = useRef(0);

    /**
     * @type {React.MutableRefObject<Number>}
     */
    const totalDragDistance = useRef(0);

    /**
     * States, unfortunately I cannot declare types for these without typescript.
     */
    const [isDraggingState, setIsDraggingState] = useState(false);
    const [disablePrevButton, setDisablePrevButton] = useState(true);
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [childSize, setChildSize] = useState(0);

    /**
     * Generic scroll function
     *
     * @param distance
     * @return {void}
     */
    const scrollBy = (distance) => {
        if (innerElementRef.current) {
            innerElementRef.current.scrollBy({
                left: distance,
                behavior: "smooth",
            });

            onScroll(null);
        }
    };

    /**
     * Exectutes whatever actions needed on `scroll` event.
     * This function is currently NOT being throttled.
     *
     * @param {Event} e
     * @return {void}
     */
    const onScroll = (e) => {
        if (!innerElementRef.current) return;

        let hasScrollableContent = innerElementRef.current.scrollWidth > innerElementRef.current.offsetWidth;
        if (!hasScrollableContent) {
            setDisablePrevButton(true);
            setDisableNextButton(true);
            return;
        }

        setDisablePrevButton(Math.floor(innerElementRef.current.scrollLeft) === 0);
        setDisableNextButton(innerElementRef.current.scrollLeft + innerElementRef.current.offsetWidth >= innerElementRef.current.scrollWidth - 2);
    }

    /**
     * Exectutes whatever actions needed on `resize` event.
     * This function is currently being throttled by `resizeEventHandler`.
     */
    const onResize = (e) => {
        if (!innerElementRef.current) return;
        setWidth(innerElementRef.current.offsetWidth);
    }

    /**
     * Handle/manage what to do, when to do and how often etc.
     * This would usually handle throttling, but that is currently not needed, see resizeEventHandler.
     * @param {Event} e
     * @return {void}
     */
    const scrollEventHandler = (e) => {
        onScroll(e);
    }

    /**
     * Calculates the width of the child elements based on the responsiveConfig prop.
     * @returns {void}
     */
    const calculateChildWidth = () => {
        if (!innerElementRef.current) return 0;

        const currentConfig = getResponsiveConfig(innerElementRef.current.offsetWidth);
        const visibleChildElement = currentConfig.settings.itemsToShow;
        const gapSizeToUse = currentConfig.settings.gap != null && !isNaN(currentConfig.settings.gap) ? currentConfig.settings.gap : gapSize;
        const totalGapSize = (Math.ceil(visibleChildElement) - 1) * gapSizeToUse;
        const availableWidth = innerElementRef.current.offsetWidth - totalGapSize;
        setChildSize(availableWidth / visibleChildElement);
        setGapSize(gapSizeToUse);
    }

    /**
     * Retrieves the best matching config based on scroll container width.
     * @param width
     * @returns {*}
     */
    function getResponsiveConfig(width) {
        if (responsiveConfig.length === 1) {
            return responsiveConfig[0];
        }

        let bestMatch = responsiveConfig[0];
        for (let i = 1; i < responsiveConfig.length; i++) {
            if (width >= responsiveConfig[i].breakpoint) {
                bestMatch = responsiveConfig[i];
            } else {
                break;
            }
        }
        return bestMatch;
    }


    /**
     * Handles MouseDown Event
     * @param {Event|MouseEvent} e
     */
    const handleMouseDown = (e) => {
        if (!innerElementRef.current) return;

        const target = e.target;
        if (!innerElementRef.current.contains(target)) return;

        e.preventDefault();

        isDragging.current = false;
        dragStartX.current = e.clientX;
        scrollStartX.current = innerElementRef.current.scrollLeft;
        totalDragDistance.current = 0;
        setIsDraggingState(false);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    /**
     * Handles MouseMove event
     * @param {Event|MouseEvent} e
     */
    const handleMouseMove = (e) => {
        if (!innerElementRef.current || dragStartX.current === null) return;

        const deltaX = e.clientX - dragStartX.current;
        totalDragDistance.current += Math.abs(e.movementX);

        if (!isDragging.current && Math.abs(deltaX) > dragThreshold) {
            isDragging.current = true;
            setIsDraggingState(true);
        }

        if (isDragging.current) {
            e.preventDefault();
            innerElementRef.current.scrollLeft = scrollStartX.current - deltaX;
        }
    };

    /**
     * Handles MouseUp event
     * @param {Event|MouseEvent} e
     */
    const handleMouseUp = (e) => {
        dragStartX.current = null;
        isDragging.current = false;
        totalDragDistance.current = 0;
        setIsDraggingState(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }

    /**
     * Handle/manage what to do, when to do and how often etc.
     * This method will wait for resize to finish before executing `onResize`.
     * @param {Event} e
     * @return {void}
     */
    const resizeEventHandler = (e) => {
        if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = window.setTimeout(() => onResize(e), 100);
    }

    const renderNav = () => {
        const props = {
            heading,
            scrollBy,
            childSize,
            gapSize,
            disablePrevButton,
            disableNextButton,
            bothNavButtonsDisabled,
            prevIcon,
            nextIcon,
            onPrev,
            onNext
        };

        switch (scrollNavigationStyle) {
            default:
            case "style-1":
                return (<NavigationStyle1 {...props} />);
            case "style-2":
                return (<NavigationStyle2 {...props} />);
            case "hidden":
                return null;
        }
    }

    useEffect(() => {
        const handleMouseLeave = () => {
            isDragging.current = false;
            setIsDraggingState(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        const container = innerElementRef.current;
        if (container) {
            container.addEventListener('mouseleave', handleMouseLeave);
            document.addEventListener('mousedown', handleMouseDown);
        }

        window.addEventListener('resize', resizeEventHandler);
        setTimeout(() => {
            onResize(null);
            calculateChildWidth();
        }, 2000);

        return () => {
            if (container) {
                container.removeEventListener('mouseleave', handleMouseLeave);
                document.removeEventListener('mousedown', handleMouseDown);
                window.removeEventListener('resize', resizeEventHandler);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            }
        };
    }, []);

    useLayoutEffect(() => {
        calculateChildWidth();
        onScroll(null);
    }, [width]);
    
    const tempHide = !numberOfChildren || childSize <= 0 ? { height: 0, overflow: 'hidden', opacity: 0 } : null;
    const bothNavButtonsDisabled = disablePrevButton && disableNextButton;

    return <div
        className={clsx(`scroll-container nav-${scrollNavigationStyle}`, {
            'hide-scroll': hideScrollBar
        })}
        style={{ ...tempHide }}
    >
        {renderNav()}

        <div
            className="scroll-container__inner"
            onScroll={scrollEventHandler}
            ref={innerElementRef}
            style={{ userSelect: "none" }}
        >
            {children.map((item, index) =>
                <div style={{
                    // TODO: convert to CSS class?
                    width: childSize,
                    flex: `0 0 ${childSize}px`,
                    pointerEvents: isDraggingState ? 'none' : 'all'
                }} key={index}>{item}</div>
            )}
        </div>
    </div>
}

/**
 *

 * @returns {JSX.Element}
 * @constructor
 */

/**
 * @param {Object} props The component props
 * @param {string} [props.heading]
 * @param {(distance) => void} [props.scrollBy]
 * @param {number} [props.childSize]
 * @param {number} [props.gapSize]
 * @param {boolean} [props.disablePrevButton]
 * @param {boolean} [props.disableNextButton]
 * @param {boolean} [props.bothNavButtonsDisabled]
 * @param {React.ReactNode|null} [props.prevIcon]
 * @param {React.ReactNode|null} [props.nextIcon]
 * @returns {JSX.Element} Navigation style 1 component/jsx
 */
const NavigationStyle1 = ({
    heading,
    scrollBy,
    childSize,
    gapSize,
    disablePrevButton,
    disableNextButton,
    bothNavButtonsDisabled,
    prevIcon,
    nextIcon,
}) => {
    return (
        <div className="scroll-container__header">
            {heading && <h3 className="mb-0">{heading}</h3>}

            <div className="scroll-container__nav">
                <button
                    type="button"
                    aria-label={translate('muha.scroll.previous')}
                    className={'scroll-container__arrow scroll-container__arrow-prev' + (disablePrevButton ? ' disabled' : '') + (bothNavButtonsDisabled ? ' hidden' : '')}
                    onClick={() => scrollBy((childSize + gapSize) * -1)}
                >
                    {prevIcon != null ? (
                        <>{prevIcon}</>
                    ) : (
                        <Svg icon="arrow" extraClass="arrow-prev" />
                    )}
                </button>

                <button
                    type="button"
                    aria-label={translate('muha.scroll.next')}
                    className={'scroll-container__arrow scroll-container__arrow-next' + (disableNextButton ? ' disabled' : '') + (bothNavButtonsDisabled ? ' hidden' : '')}
                    onClick={() => scrollBy(childSize + gapSize)}
                >
                    {nextIcon != null ? (
                        <>{nextIcon}</>
                    ) : (
                        <Svg icon="arrow" extraClass="arrow-next" />
                    )}
                </button>
            </div>
        </div>
    );
}

/**
 * @param {Object} props The component props
 * @param {(distance) => void} [props.scrollBy]
 * @param {number} [props.childSize]
 * @param {number} [props.gapSize]
 * @param {boolean} [props.disablePrevButton]
 * @param {boolean} [props.disableNextButton]
 * @param {boolean} [props.bothNavButtonsDisabled]
 * @param {React.ReactNode|null} [props.prevIcon]
 * @param {React.ReactNode|null} [props.nextIcon]
 * @returns {JSX.Element} Navigation style 2 component/jsx
 */
const NavigationStyle2 = ({
    scrollBy,
    childSize,
    gapSize,
    disablePrevButton,
    disableNextButton,
    bothNavButtonsDisabled,
    prevIcon,
    nextIcon,
    onPrev,
    onNext
}) => {
    return (
        <div className="scroll-container__nav">
            <button
                type="button"
                aria-label={translate('muha.scroll.previous')}
                className={'scroll-container__arrow scroll-container__arrow-prev' + (disablePrevButton ? ' disabled' : '') + (bothNavButtonsDisabled ? ' hidden' : '')}
                onClick={() => {
                    scrollBy((childSize + gapSize) * -1);
                    if (onPrev) onPrev();
                }}
            >
                {prevIcon != null ? (
                    <>{prevIcon}</>
                ) : (
                    <Svg icon="arrow" extraClass="arrow-prev" />
                )}
            </button>

            <button
                type="button"
                aria-label={translate('muha.scroll.next')}
                className={'scroll-container__arrow scroll-container__arrow-next' + (disableNextButton ? ' disabled' : '') + (bothNavButtonsDisabled ? ' hidden' : '')}
                onClick={() => {
                    scrollBy(childSize + gapSize);
                    if (onNext) onNext();
                }}
            >
                {nextIcon != null ? (
                    <>{nextIcon}</>
                ) : (
                    <Svg icon="arrow" extraClass="arrow-next" />
                )}
            </button>
        </div>
    );
}

export default ScrollContainer;
